/*
  Health & Dietary Information form (snippets/health-info-form.liquid).
  Saves answers to cart attributes over AJAX so they reach the order even through dynamic checkout buttons,
  and keeps them through the cart drawer's section re-render (which replaces the whole form element).
  Optional collection only: checkout is never blocked.
*/
(function () {
  if (customElements.get('health-info-form')) return;

  /* Shared by every instance (drawer + cart page) and by the re-rendered copies of the same form. */
  const shared = { values: null, open: null, timer: null, saving: false, dirty: false, clearing: false, request: null };

  const updateCart = (attributes) =>
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes })
    }).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.json();
    });

  class HealthInfoForm extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('details');
      this.status = this.querySelector('[data-status]');
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSync = this.onSync.bind(this);

      if (shared.values) this.write(shared.values);
      if (shared.open !== null && this.panel) this.panel.open = shared.open;

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      if (this.panel) this.panel.addEventListener('toggle', () => { shared.open = this.panel.open; });
      document.addEventListener('health-info:sync', this.onSync);
    }

    disconnectedCallback() {
      document.removeEventListener('health-info:sync', this.onSync);
      /* A re-render landed inside the debounce window: save now, the values are already in `shared`. */
      if (shared.timer) { clearTimeout(shared.timer); shared.timer = null; this.save(); }
    }

    read() {
      return { notes: this.querySelector('[data-field="notes"]').value };
    }

    write(values) {
      const field = this.querySelector('[data-field="notes"]');
      if (document.activeElement !== field) field.value = values.notes;
    }

    queueSave(delay) {
      shared.values = this.read();
      document.dispatchEvent(new CustomEvent('health-info:sync', { detail: { source: this } }));
      clearTimeout(shared.timer);
      shared.timer = setTimeout(() => { shared.timer = null; this.save(); }, delay);
    }

    onInput(event) {
      if (event.target.matches('[data-field="notes"]')) this.queueSave(500);
    }

    onChange(event) {
      if (event.target.matches('[data-field="notes"]')) this.queueSave(0);
    }

    onSync(event) {
      if (event.detail.source === this || !shared.values) return;
      this.write(shared.values);
    }

    toAttributes(values) {
      return {
        [this.dataset.keyNotes]: values.notes.trim(),
        [this.dataset.keyConditions]: '',
        [this.dataset.keyAllergies]: '',
        [this.dataset.keyDiet]: ''
      };
    }

    setStatus(text, state) {
      document.querySelectorAll('health-info-form').forEach((form) => {
        const status = form.querySelector('[data-status]');
        if (!status) return;
        status.textContent = text;
        status.dataset.state = state;
      });
    }

    save() {
      if (!shared.values || shared.clearing) return;
      if (shared.saving) { shared.dirty = true; return; }
      shared.saving = true;
      shared.dirty = false;
      const dataset = this.dataset;
      this.setStatus(dataset.savingText, 'saving');
      shared.request = updateCart(this.toAttributes(shared.values))
        .then(() => this.setStatus(dataset.savedText, 'saved'))
        .catch(() => this.setStatus(dataset.errorText, 'error'))
        .finally(() => {
          shared.saving = false;
          shared.request = null;
          if (shared.dirty) this.save();
        });
    }
  }

  /* Rendered by the snippet when the cart no longer holds an eligible product but still carries answers. */
  class HealthInfoClear extends HTMLElement {
    connectedCallback() {
      if (shared.clearing) return;
      shared.clearing = true;
      clearTimeout(shared.timer);
      shared.timer = null;
      shared.values = null;
      shared.open = null;
      shared.dirty = false;
      const empty = { [this.dataset.keyNotes]: '', [this.dataset.keyConditions]: '', [this.dataset.keyAllergies]: '', [this.dataset.keyDiet]: '' };
      /* Let a save that is already on the wire finish first, so it cannot land after the clear. */
      Promise.resolve(shared.request)
        .then(() => updateCart(empty))
        .catch(() => {})
        .finally(() => { shared.clearing = false; });
    }
  }

  /* Cart page only: the slot sits outside the part of the page that cart.js re-renders, so it refreshes itself. */
  class HealthInfoSlot extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    refresh() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
        .then((response) => response.text())
        .then((text) => {
          const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('health-info-slot');
          if (!fresh) return;
          const hadForm = !!this.querySelector('health-info-form');
          const hasForm = !!fresh.querySelector('health-info-form');
          if (hadForm !== hasForm || (!hasForm && fresh.querySelector('health-info-clear'))) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    }
  }

  customElements.define('health-info-form', HealthInfoForm);
  customElements.define('health-info-clear', HealthInfoClear);
  customElements.define('health-info-slot', HealthInfoSlot);
})();
